import * as z from "zod";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CheckCircle2, Info } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import FormPermuta from "@/components/vendi/FormPermuta";
import PermutaLegacy from "@/components/Checkout/PermutaLegacy";
import InfoCard from "@/components/permuta/InfoCard";

const FormSchema = z.object({
    rottamazioneOption: z.enum(["rottamazione", "no-rottamazione"]),
    permutaOption: z.enum(["permuta", "no-permuta"]),
});

const permutaInfoSteps = [
    {
        number: "1",
        title: "Inserisci i dati del tuo veicolo",
        description:
            "Compila il form con i dati del veicolo e fissa un appuntamento in sede per effettuare una perizia immediata. In questo modo potremo iniziare a preparare una prima valutazione con valore minimo garantito, con la massima sicurezza e trasparenza.",
    },
    {
        number: "2",
        title: "Perizia in sede e vendita rapida",
        description:
            "Vieni da Gruppo Piccirillo: completeremo la perizia in sede e definiremo una stima reale di vendita. Con \"Comprami Subito\" o con il nostro servizio d'asta, la tua auto potrà essere venduta in brevissimo tempo, al miglior prezzo possibile.",
    },
    {
        number: "3",
        title: "Permuta gratuita e scegli la tua prossima auto",
        description:
            "Il servizio di permuta è gratuito per i clienti che acquistano un'auto da Gruppo Piccirillo S.p.A.. Vendi la tua auto e scegli la prossima tra oltre 3.000 veicoli. Crea in autonomia un preventivo chiaro e completo, con servizi, accessori, finanziamenti e tutti i dati economici sempre trasparenti. Scopri il parco veicoli su gruppopiccirillo.it/parco-veicoli.",
    },
];

const permutaInfoBenefits = [
    {
        title: "Nessun limite sul modello e il prezzo dell'auto!",
        description: "A differenza dei nostri competitor non abbiamo limiti di spesa, qualsiasi sia il modello!",
    },
    {
        title: "Massimo valore possibile",
        description: "Più concorrenza tra acquirenti = maggiore probabilità di ottenere una valutazione migliore.",
    },
    {
        title: "Minimo garantito",
        description: "Se il veicolo non viene venduto entro 30 giorni, puoi accettare il minimo garantito stabilito da Gruppo Piccirillo.",
    },
    {
        title: "Libertà di scelta",
        description: "Per 30 giorni hai attiva anche l'opzione \"Comprami Subito\", con vendita immediata al valore minimo garantito.",
    },
];

type PermutaInfoCard = {
    _id?: string;
    number: number | string;
    title: string;
    description: string;
    imageUrl?: string;
    imageCar?: string;
    imagePosition?: "left" | "right";
};

type CheckoutPermutaSelection = z.infer<typeof FormSchema>;

interface PermutaProps {
    onSubmit: (data: any) => void;
    goBack: () => void;
    onChange?: (data: { option: string }) => void;
    useNewPermutaFlow: boolean;
    customerData?: {
        nome?: string;
        cognome?: string;
        email?: string;
        telefono?: string;
        provincia?: string;
    };
    initialData?: PermutaData;
}

const Permuta = ({ onSubmit, goBack, onChange, useNewPermutaFlow, customerData, initialData }: PermutaProps) => {
    if (!useNewPermutaFlow) {
        return <PermutaLegacy onSubmit={onSubmit} goBack={goBack} onChange={onChange} />;
    }

    const [queryClient] = useState(() => new QueryClient());
    const defaultValues = useMemo<CheckoutPermutaSelection>(() => {
        const option = initialData?.option;
        return {
            rottamazioneOption: option === "rottamazione" ? "rottamazione" : "no-rottamazione",
            permutaOption: option === "permuta" ? "permuta" : "no-permuta",
        };
    }, [initialData?.option]);

    const form = useForm<CheckoutPermutaSelection>({
        resolver: zodResolver(FormSchema),
        defaultValues,
    });

    const [permutaModalOpen, setPermutaModalOpen] = useState(false);
    const [permutaInfoModalOpen, setPermutaInfoModalOpen] = useState(false);
    const [sanityPermutaInfoCards, setSanityPermutaInfoCards] = useState<PermutaInfoCard[] | null>(null);
    const [permutaInfoLoading, setPermutaInfoLoading] = useState(false);
    const [permutaCompleted, setPermutaCompleted] = useState<boolean>(
        Boolean(initialData?.permutaCompleted || initialData?.permutaId),
    );
    const [permutaPayload, setPermutaPayload] = useState<any>(
        initialData?.option === "permuta" ? initialData : null,
    );

    const rottamazioneOption = form.watch("rottamazioneOption");
    const permutaOption = form.watch("permutaOption");
    const isPermutaSectionDisabled = rottamazioneOption === "rottamazione";
    const isRottamazioneYesDisabled = permutaOption === "permuta";

    useEffect(() => {
        const currentOption =
            permutaOption === "permuta"
                ? "permuta"
                : rottamazioneOption === "rottamazione"
                    ? "rottamazione"
                    : "prenotazione";
        onChange?.({ option: currentOption });
    }, [onChange, permutaOption, rottamazioneOption]);

    useEffect(() => {
        if (!initialData) return;
        setPermutaCompleted(Boolean(initialData.permutaCompleted || initialData.permutaId));
        if (initialData.option === "permuta") {
            setPermutaPayload(initialData);
        }
    }, [initialData]);

    useEffect(() => {
        if (!permutaInfoModalOpen || sanityPermutaInfoCards !== null || permutaInfoLoading) return;

        let isCancelled = false;
        setPermutaInfoLoading(true);

        fetch("/api/permuta/info-cards")
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Errore nel recupero delle info card permuta");
                }
                return response.json();
            })
            .then((data) => {
                if (isCancelled) return;
                setSanityPermutaInfoCards(Array.isArray(data?.infoCards) ? data.infoCards : []);
            })
            .catch((error) => {
                if (isCancelled) return;
                console.error(error);
                setSanityPermutaInfoCards([]);
            })
            .finally(() => {
                if (!isCancelled) {
                    setPermutaInfoLoading(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [permutaInfoModalOpen, permutaInfoLoading, sanityPermutaInfoCards]);

    const displayedPermutaInfoCards = useMemo<PermutaInfoCard[]>(() => {
        if (sanityPermutaInfoCards?.length) {
            return sanityPermutaInfoCards;
        }

        return permutaInfoSteps;
    }, [sanityPermutaInfoCards]);

    const handleRottamazioneChange = (value: "rottamazione" | "no-rottamazione") => {
        if (value === "rottamazione" && isRottamazioneYesDisabled) return;
        form.setValue("rottamazioneOption", value, { shouldDirty: true, shouldValidate: true });
        if (value === "rottamazione") {
            form.setValue("permutaOption", "no-permuta", { shouldDirty: true, shouldValidate: true });
        }
    };

    const handlePermutaChange = (value: "permuta" | "no-permuta") => {
        form.setValue("permutaOption", value, { shouldDirty: true, shouldValidate: true });
        if (value === "permuta") {
            form.setValue("rottamazioneOption", "no-rottamazione", { shouldDirty: true, shouldValidate: true });
        }
    };

    const handlePermutaCompleted = (payload: {
        permutaId?: string;
        productId?: string;
        APERMUTA?: string;
        codiceLead?: string;
        permutaLeadCode?: string;
        valoreDesiderato?: string;
        targa?: string;
        marca?: string;
        modello?: string;
        versione?: string;
        meseImmatricolazione?: string;
        annoImmatricolazione?: string;
        km?: string;
        valutazioneStimata?: PermutaData["valutazioneStimata"];
        permutaCompleted: boolean;
    }) => {
        const nextPayload: PermutaData = {
            ...payload,
            permutaLeadCode: payload.permutaLeadCode ?? payload.codiceLead,
        };
        setPermutaPayload(nextPayload);
        setPermutaCompleted(true);
        form.setValue("permutaOption", "permuta", { shouldDirty: true, shouldValidate: true });
        form.setValue("rottamazioneOption", "no-rottamazione", { shouldDirty: true, shouldValidate: true });
        setPermutaModalOpen(false);
    };

    const isForwardDisabled = permutaOption === "permuta" && !permutaCompleted;

    const handleSubmit = (values: CheckoutPermutaSelection) => {
        const persistedPermutaData = permutaPayload
            ? { ...permutaPayload, permutaCompleted: permutaCompleted || Boolean(permutaPayload?.permutaId) }
            : {};

        if (values.permutaOption === "permuta") {
            if (!permutaCompleted) return;
            onSubmit({
                option: "permuta",
                ...persistedPermutaData,
            });
            return;
        }

        if (values.rottamazioneOption === "rottamazione") {
            onSubmit({ option: "rottamazione", ...persistedPermutaData });
            return;
        }

        onSubmit({ option: "prenotazione", ...persistedPermutaData });
    };

    return (
        <div className="flex flex-col">
            <h1 className="py-5 text-2xl font-bold">Rottamazione e Permuta dell'usato</h1>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="flex h-[85%] flex-col justify-between space-y-8">
                    <div className="space-y-6">
                        <FormField
                            control={form.control}
                            name="rottamazioneOption"
                            render={({ field }) => (
                                <FormItem>
                                    <RadioGroup
                                        onValueChange={(value) => handleRottamazioneChange(value as "rottamazione" | "no-rottamazione")}
                                        value={field.value}
                                        className="mt-4 divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white shadow-sm"
                                    >
                                        <FormItem
                                            className={`flex items-start gap-4 px-6 py-5 transition ${field.value === "rottamazione" ? "rounded-t-2xl bg-blue-50" : ""} ${isRottamazioneYesDisabled ? "opacity-60" : ""}`}
                                        >
                                            <FormControl>
                                                <RadioGroupItem
                                                    value="rottamazione"
                                                    disabled={isRottamazioneYesDisabled}
                                                    className="mt-1 h-5 w-5 border-2 border-blue-300 text-blue-600 shadow-sm data-[state=checked]:border-blue-600 data-[state=checked]:text-blue-600 data-[state=checked]:bg-blue-50"
                                                />
                                            </FormControl>
                                            <div className="flex-1 !mt-0">
                                                <FormLabel className="!mt-0 font-semibold text-gray-900">
                                                    Sì, voglio rottamare il mio usato
                                                </FormLabel>
                                                <p className="text-sm text-gray-500">Bonus rottamazione 500€ applicato al totale.</p>
                                            </div>
                                        </FormItem>
                                        <FormItem
                                            className={`flex items-start gap-4 px-6 py-5 transition ${field.value === "no-rottamazione" ? "rounded-b-2xl bg-gray-50" : ""}`}
                                        >
                                            <FormControl>
                                                <RadioGroupItem
                                                    value="no-rottamazione"
                                                    className="mt-1 h-5 w-5 border-2 border-blue-200 text-blue-500 shadow-sm data-[state=checked]:border-blue-600 data-[state=checked]:text-blue-600 data-[state=checked]:bg-blue-50"
                                                />
                                            </FormControl>
                                            <div className="flex-1 !mt-0">
                                                <FormLabel className="!mt-0 font-semibold text-gray-900">
                                                    No, preferisco non rottamare la mia auto
                                                </FormLabel>
                                                <p className="text-sm text-gray-500">Prosegui direttamente con la prenotazione.</p>
                                            </div>
                                        </FormItem>
                                    </RadioGroup>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="permutaOption"
                            render={({ field }) => (
                                <FormItem>
                                    <h2 className={`text-lg font-semibold ${isPermutaSectionDisabled ? "text-gray-400" : "text-gray-900"}`}>Vuoi dare in Permuta la tua auto usata?</h2>
                                    <RadioGroup
                                        onValueChange={(value) => handlePermutaChange(value as "permuta" | "no-permuta")}
                                        value={field.value}
                                        className="mt-3 divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white shadow-sm"
                                    >
                                        <FormItem
                                            className={`space-y-3 px-6 py-5 transition ${field.value === "permuta" ? "rounded-t-2xl bg-blue-50" : ""} ${isPermutaSectionDisabled ? "opacity-60" : ""}`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <FormControl>
                                                    <RadioGroupItem
                                                        value="permuta"
                                                        disabled={isPermutaSectionDisabled}
                                                        className="mt-1 h-5 w-5 border-2 border-blue-300 text-blue-600 shadow-sm data-[state=checked]:border-blue-600 data-[state=checked]:text-blue-600 data-[state=checked]:bg-blue-50"
                                                    />
                                                </FormControl>
                                                <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-1 !mt-0">
                                                    <FormLabel className="!mt-0 font-semibold text-gray-900">
                                                        Sì, voglio la permuta del mio usato
                                                    </FormLabel>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        className="h-auto px-0 text-sm font-medium text-primary hover:bg-transparent"
                                                        onClick={() => setPermutaInfoModalOpen(true)}
                                                    >
                                                        <Info className="mr-2 h-4 w-4" />
                                                        Informazioni aggiuntive
                                                    </Button>
                                                </div>
                                            </div>

                                            {field.value === "permuta" && (
                                                <div className="pl-9">
                                                    {!permutaCompleted ? (
                                                        <Button
                                                            type="button"
                                                            disabled={isPermutaSectionDisabled}
                                                            className="text-white bg-gradient-custom"
                                                            onClick={() => setPermutaModalOpen(true)}
                                                        >
                                                            Inizia permuta
                                                        </Button>
                                                    ) : (
                                                        <div className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-sm font-semibold text-green-700">
                                                            <CheckCircle2 className="h-4 w-4" />
                                                            Dati permuta completi
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </FormItem>

                                        <FormItem
                                            className={`flex items-start gap-4 px-6 py-5 transition ${field.value === "no-permuta" ? "rounded-b-2xl bg-gray-50" : ""} ${isPermutaSectionDisabled ? "opacity-60" : ""}`}
                                        >
                                            <FormControl>
                                                <RadioGroupItem
                                                    value="no-permuta"
                                                    disabled={isPermutaSectionDisabled}
                                                    className="mt-1 h-5 w-5 border-2 border-blue-200 text-blue-500 shadow-sm data-[state=checked]:border-blue-600 data-[state=checked]:text-blue-600 data-[state=checked]:bg-blue-50"
                                                />
                                            </FormControl>
                                            <div className="flex-1 !mt-0">
                                                <FormLabel className="!mt-0 font-semibold text-gray-900">
                                                    No, non ho vettura per la permuta
                                                </FormLabel>
                                            </div>
                                        </FormItem>
                                    </RadioGroup>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="flex w-full items-center justify-between">
                        <Button onClick={goBack} type="button" variant="ghost" className="text-primary hover:bg-transparent">
                            Indietro
                        </Button>
                        <Button disabled={isForwardDisabled} className="sm:min-w-64 text-white bg-gradient-custom">
                            Avanti
                        </Button>
                    </div>
                </form>
            </Form>

            <Dialog open={permutaModalOpen} onOpenChange={setPermutaModalOpen}>
                <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-5xl">
                    <DialogHeader>
                        <DialogTitle>Inizia permuta</DialogTitle>
                        <DialogDescription>
                            Completa il flusso di permuta. Al termine il lead verrà inviato e potrai proseguire.
                        </DialogDescription>
                    </DialogHeader>

                    <QueryClientProvider client={queryClient}>
                        <FormPermuta
                            embeddedMode={true}
                            forcePermutaOnly={true}
                            initialCustomerData={{
                                nome: customerData?.nome,
                                cognome: customerData?.cognome,
                                email: customerData?.email,
                                telefono: customerData?.telefono,
                                provincia: customerData?.provincia,
                            }}
                            onCompleted={handlePermutaCompleted}
                        />
                    </QueryClientProvider>
                </DialogContent>
            </Dialog>

            <Dialog open={permutaInfoModalOpen} onOpenChange={setPermutaInfoModalOpen}>
                <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-6xl">
                    <DialogHeader>
                        <DialogTitle>Informazioni aggiuntive sulla permuta</DialogTitle>
                        <DialogDescription>
                            Come funziona il servizio di permuta Gruppo Piccirillo.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-8">
                        {permutaInfoLoading && sanityPermutaInfoCards === null ? (
                            <div className="rounded-xl border border-gray-200 bg-slate-50 p-6 text-sm text-gray-600">
                                Caricamento informazioni da Sanity...
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {displayedPermutaInfoCards.map((card) => {
                                    const hasSanityImages = Boolean(card.imageUrl && card.imageCar && card.imagePosition);

                                    if (hasSanityImages) {
                                        return (
                                            <InfoCard
                                                key={card._id ?? card.number}
                                                number={Number(card.number)}
                                                title={card.title}
                                                description={card.description}
                                                imageUrl={card.imageUrl as string}
                                                imageCar={card.imageCar as string}
                                                imagePosition={card.imagePosition as "left" | "right"}
                                            />
                                        );
                                    }

                                    return (
                                        <div key={card._id ?? card.number} className="rounded-xl border border-gray-200 bg-slate-50 p-5">
                                            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-lg font-bold text-white">
                                                {card.number}
                                            </div>
                                            <h3 className="text-base font-semibold text-gray-900">{card.title}</h3>
                                            <p className="mt-2 text-sm leading-6 text-gray-600">{card.description}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Perché scegliere la permuta online</h3>
                            <div className="mt-4 grid gap-4 md:grid-cols-2">
                                {permutaInfoBenefits.map((benefit) => (
                                    <div key={benefit.title} className="rounded-xl border border-gray-200 bg-white p-5">
                                        <h4 className="text-base font-semibold text-gray-900">{benefit.title}</h4>
                                        <p className="mt-2 text-sm leading-6 text-gray-600">{benefit.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Permuta;
